import React from 'react';

export function useEscapeClose(active: boolean, onClose: () => void): void {
    const closeRef = React.useRef(onClose);
    React.useEffect(() => {
        closeRef.current = onClose;
    }, [onClose]);
    React.useEffect(() => {
        if (!active) return undefined;
        const previous = document.activeElement as HTMLElement | null;
        const dialogs = document.querySelectorAll<HTMLElement>(
            '[role="dialog"][aria-modal="true"]'
        );
        const dialog = dialogs[dialogs.length - 1];
        if (!dialog) return undefined;
        const controls = (): HTMLElement[] =>
            Array.from(
                dialog.querySelectorAll<HTMLElement>(
                    [
                        'a[href]',
                        'button:not(:disabled)',
                        'input:not(:disabled)',
                        'select:not(:disabled)',
                        'textarea:not(:disabled)',
                        '[tabindex]:not([tabindex="-1"])',
                    ].join(', ')
                )
            ).filter(element => element.getClientRects().length > 0);
        const frame = window.requestAnimationFrame(() =>
            controls()[0]?.focus({ preventScroll: true })
        );
        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeRef.current();
            }
            if (event.key !== 'Tab') return;
            const items = controls();
            const first = items[0];
            const last = items[items.length - 1];
            if (
                event.shiftKey &&
                (document.activeElement === first ||
                    !dialog.contains(document.activeElement))
            ) {
                event.preventDefault();
                last?.focus();
            } else if (
                !event.shiftKey &&
                (document.activeElement === last ||
                    !dialog.contains(document.activeElement))
            ) {
                event.preventDefault();
                first?.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener('keydown', onKeyDown);
            if (previous?.isConnected) previous.focus({ preventScroll: true });
        };
    }, [active]);
}
export function useLockBodyScroll(active: boolean): void {
    React.useEffect(() => {
        if (!active) return undefined;
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = overflow;
        };
    }, [active]);
}
